import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'withdrawn';

export type QuoteLineSource = 'manual' | 'service' | 'custom';

export interface QuoteRow {
  id: string;
  clinic_id: string;
  patient_id: string;
  quote_number: string | null;
  status: QuoteStatus;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  vat_cents: number;
  total_cents: number;
  notes: string | null;
  valid_until: string | null;
  patient_snapshot: Record<string, unknown>;
  clinic_snapshot: Record<string, unknown>;
  accept_token_hash: string | null;
  accept_token_expires_at: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  expired_at: string | null;
  withdrawn_at: string | null;
  converted_invoice_id: string | null;
  converted_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuoteLineRow {
  id: string;
  clinic_id: string;
  quote_id: string;
  position: number;
  source: QuoteLineSource;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  vat_rate_percent: number;
  line_total_cents: number;
  created_at: string;
}

export interface QuoteDetail extends QuoteRow {
  lines: QuoteLineRow[];
}

export interface QuoteListItem {
  id: string;
  clinic_id: string;
  patient_id: string;
  quote_number: string | null;
  status: QuoteStatus;
  currency: string;
  total_cents: number;
  valid_until: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
  patient_first_name: string | null;
  patient_last_name: string | null;
}

export interface QuoteListFilters {
  status?: QuoteStatus | 'all';
  search?: string;
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const quotesListQueryKey = (clinicId: string | null, filters: QuoteListFilters) =>
  ['quotes', 'list', clinicId, filters] as const;

export const quoteDetailQueryKey = (quoteId: string | null | undefined) =>
  ['quotes', 'detail', quoteId] as const;

// ── useQuotesList ─────────────────────────────────────────────────────────────

export function useQuotesList(clinicId: string | null | undefined, filters: QuoteListFilters) {
  return useQuery({
    queryKey: quotesListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<QuoteListItem[]> => {
      if (!clinicId) return [];
      let q = supabase
        .from('quotes')
        .select(
          `id, clinic_id, patient_id, quote_number, status, currency,
           total_cents, valid_until, sent_at, accepted_at, created_at, updated_at,
           patient:patients(first_name, last_name)`,
        )
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (filters.status && filters.status !== 'all') {
        q = q.eq('status', filters.status);
      }

      const search = filters.search?.trim();
      if (search) {
        q = q.or(`quote_number.ilike.%${search}%`);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row): QuoteListItem => {
        const patient = (
          row as unknown as {
            patient: { first_name: string; last_name: string } | null;
          }
        ).patient;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          patient_id: row.patient_id,
          quote_number: row.quote_number,
          status: row.status as QuoteStatus,
          currency: row.currency,
          total_cents: row.total_cents,
          valid_until: row.valid_until,
          sent_at: row.sent_at,
          accepted_at: row.accepted_at,
          created_at: row.created_at,
          updated_at: row.updated_at,
          patient_first_name: patient?.first_name ?? null,
          patient_last_name: patient?.last_name ?? null,
        };
      });
    },
    staleTime: 15_000,
  });
}

// ── useQuote ──────────────────────────────────────────────────────────────────

export function useQuote(quoteId: string | null | undefined) {
  return useQuery({
    queryKey: quoteDetailQueryKey(quoteId ?? null),
    enabled: Boolean(quoteId),
    queryFn: async (): Promise<QuoteDetail> => {
      if (!quoteId) throw new Error('No quote id');
      const [quote, lines] = await Promise.all([
        supabase.from('quotes').select('*').eq('id', quoteId).single(),
        supabase
          .from('quote_line_items')
          .select('*')
          .eq('quote_id', quoteId)
          .order('position', { ascending: true }),
      ]);
      if (quote.error) throw quote.error;
      if (lines.error) throw lines.error;
      return {
        ...(quote.data as unknown as QuoteRow),
        lines: (lines.data ?? []) as unknown as QuoteLineRow[],
      };
    },
    staleTime: 5_000,
  });
}

// ── useCreateQuoteDraft ───────────────────────────────────────────────────────

export interface CreateQuoteDraftInput {
  clinic_id: string;
  patient_id: string;
  notes?: string;
  valid_until?: string;
}

export function useCreateQuoteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateQuoteDraftInput): Promise<QuoteRow> => {
      const { data, error } = await supabase.rpc('create_quote_draft', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id,
        p_notes: input.notes,
        p_valid_until: input.valid_until,
      });
      if (error) throw error;
      return data as unknown as QuoteRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['quotes', 'list', row.clinic_id] });
    },
  });
}

// ── useUpdateQuoteDraft ───────────────────────────────────────────────────────

export interface UpdateQuoteDraftInput {
  id: string;
  notes?: string;
  valid_until?: string;
}

export function useUpdateQuoteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateQuoteDraftInput): Promise<QuoteRow> => {
      const { data, error } = await supabase.rpc('update_quote_draft', {
        p_id: input.id,
        p_notes: input.notes,
        p_valid_until: input.valid_until,
      });
      if (error) throw error;
      return data as unknown as QuoteRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['quotes', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(row.id) });
    },
  });
}

// ── useAddQuoteLine ───────────────────────────────────────────────────────────

export interface AddQuoteLineInput {
  quote_id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  vat_rate_percent?: number;
  position?: number;
  source?: QuoteLineSource;
}

export function useAddQuoteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddQuoteLineInput): Promise<QuoteLineRow> => {
      const { data, error } = await supabase.rpc('add_quote_line', {
        p_quote_id: input.quote_id,
        p_description: input.description,
        p_quantity: input.quantity,
        p_unit: input.unit,
        p_unit_price_cents: input.unit_price_cents,
        p_vat_rate_percent: input.vat_rate_percent,
        p_position: input.position,
        p_source: input.source,
      });
      if (error) throw error;
      return data as unknown as QuoteLineRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(row.quote_id) });
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

// ── useUpdateQuoteLine ────────────────────────────────────────────────────────

export interface UpdateQuoteLineInput {
  line_id: string;
  quote_id: string;
  description?: string;
  quantity?: number;
  unit?: string;
  unit_price_cents?: number;
  vat_rate_percent?: number;
  position?: number;
}

export function useUpdateQuoteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateQuoteLineInput): Promise<QuoteLineRow> => {
      const { data, error } = await supabase.rpc('update_quote_line', {
        p_line_id: input.line_id,
        p_description: input.description,
        p_quantity: input.quantity,
        p_unit: input.unit,
        p_unit_price_cents: input.unit_price_cents,
        p_vat_rate_percent: input.vat_rate_percent,
        p_position: input.position,
      });
      if (error) throw error;
      return data as unknown as QuoteLineRow;
    },
    onSuccess: (_row, vars) => {
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(vars.quote_id) });
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

// ── useRemoveQuoteLine ────────────────────────────────────────────────────────

export interface RemoveQuoteLineInput {
  line_id: string;
  quote_id: string;
}

export function useRemoveQuoteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveQuoteLineInput): Promise<boolean> => {
      const { data, error } = await supabase.rpc('remove_quote_line', {
        p_line_id: input.line_id,
      });
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: (_ok, vars) => {
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(vars.quote_id) });
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

// ── useSendQuote ──────────────────────────────────────────────────────────────

export interface SendQuoteResult {
  quote_id: string;
  quote_number: string;
  accept_token: string;
  accept_url: string;
}

export function useSendQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quoteId: string): Promise<SendQuoteResult> => {
      const { data, error } = await supabase.rpc('send_quote', {
        p_id: quoteId,
        p_token_ttl_days: 30,
      });
      if (error) throw error;
      return data as unknown as SendQuoteResult;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(result.quote_id) });
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

// ── useWithdrawQuote ──────────────────────────────────────────────────────────

export interface WithdrawQuoteInput {
  quote_id: string;
  reason?: string;
}

export function useWithdrawQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WithdrawQuoteInput): Promise<QuoteRow> => {
      const { data, error } = await supabase.rpc('withdraw_quote', {
        p_id: input.quote_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as QuoteRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['quotes', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(row.id) });
    },
  });
}

// ── useConvertQuoteToInvoice ──────────────────────────────────────────────────

export function useConvertQuoteToInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quoteId: string) => {
      const { data, error } = await supabase.rpc('convert_quote_to_invoice', {
        p_id: quoteId,
      });
      if (error) throw error;
      return data as unknown as { id: string; clinic_id: string };
    },
    onSuccess: (invoice, quoteId) => {
      qc.invalidateQueries({ queryKey: quoteDetailQueryKey(quoteId) });
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list', invoice.clinic_id] });
    },
  });
}

// ── Public helper (called by AcceptQuote page) ────────────────────────────────

export interface PublicQuoteView {
  quote_id: string;
  quote_number: string | null;
  status: string;
  currency: string;
  total_cents: number;
  subtotal_cents: number;
  vat_cents: number;
  valid_until: string | null;
  expired: boolean;
  notes: string | null;
  clinic_name: string | null;
  clinic_address: unknown;
  lines: Array<{
    position: number;
    description: string;
    quantity: number;
    unit: string;
    unit_price_cents: number;
    vat_rate_percent: number;
    line_total_cents: number;
  }>;
}

export async function fetchPublicQuote(token: string): Promise<PublicQuoteView> {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-quote-as-patient?token=${encodeURIComponent(token)}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  );
  const json = (await res.json()) as {
    ok: boolean;
    data?: PublicQuoteView;
    error?: { code: string; message: string };
  };
  if (!json.ok || !json.data) {
    const code = json.error?.code ?? 'internal-error';
    throw Object.assign(new Error(json.error?.message ?? 'Failed to load quote'), { code });
  }
  return json.data;
}

export async function submitQuoteAction(
  token: string,
  action: 'accept' | 'reject',
  reason?: string,
): Promise<{ status: string }> {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-quote-as-patient`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, action, reason }),
    },
  );
  const json = (await res.json()) as {
    ok: boolean;
    data?: { status: string };
    error?: { code: string; message: string };
  };
  if (!json.ok || !json.data) {
    const code = json.error?.code ?? 'internal-error';
    throw Object.assign(new Error(json.error?.message ?? 'Action failed'), { code });
  }
  return json.data;
}

// ── Utilities ─────────────────────────────────────────────────────────────────

export function formatMoney(cents: number, currency = 'DKK'): string {
  return (cents / 100).toLocaleString('da-DK', { style: 'currency', currency });
}
